import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
interface Company {
  name: string;
  description: string;
}
const companies: Company[] = [{
  name: 'Forrester',
  description: 'Gave CTOs and CEOs the information they needed to confidently invest in the right software solutions.'
}, {
  name: 'Discovery Communications',
  description: 'Helped TV producers navigate millions of hours of footage to find the perfect asset for their program, without going into the field with a film crew.'
}, {
  name: 'Ontario Teachers Pension Plan',
  description: 'Provided alternative investment managers the data they needed to confidently move forward with new investment opportunities.'
}, {
  name: 'Ford',
  description: 'Helped car designers choose the right parts for their build, saving time and money by repurposing ones from other cars and trucks.'
}, {
  name: 'Expedia',
  description: 'Gave travel merchandisers a quick and easy way to surface the best deals to customers during their vacation planning.'
}, {
  name: 'Aetna',
  description: 'Provided executives insight into their ACO performance, from financial stability to staffing and quality of care.'
}, {
  name: 'Illumina',
  description: 'Gave health-conscious individuals insight into their genome and an awareness of what their future health may hold.'
}, {
  name: 'Boehringer Ingelheim',
  description: 'Gave schizophrenia patients and their caregivers a mobile coping companion and greater connection to one another.'
}, {
  name: 'CareCentrix',
  description: 'Freed home health nurses from routine tasks so they could focus on their patients immediate needs.'
}, {
  name: 'NextCode Genomics',
  description: 'Empowered parents of children with rare diseases with the genomic data needed to accelerate life-saving treatments.'
}, {
  name: 'LabCorp',
  description: 'Helped proactive health consumers get to the bottom of their concerns with a marketplace of private, home test kits.'
}, {
  name: 'Aetna CarePass',
  description: 'Allowed health plan members to connect any wearable device to their medical account, providing a clear and complete picture of their health.'
}];
function LogoCard({
  company,
  index
}: {
  company: Company;
  index: number;
}) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  return <motion.div className="flex-shrink-0 w-64 h-40 perspective-1000" onHoverStart={() => {
    setIsHovered(true);
    setIsFlipped(true);
  }} onHoverEnd={() => {
    setIsHovered(false);
    setIsFlipped(false);
  }} initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.1
  }} data-id="element-133">
      <motion.div className="relative w-full h-full" animate={{
      rotateY: isFlipped ? 180 : 0
    }} transition={{
      duration: 0.6,
      ease: [0.4, 0, 0.2, 1]
    }} style={{
      transformStyle: 'preserve-3d'
    }} data-id="element-134">
        {/* Front of card - Logo */}
        <div className="absolute inset-0 bg-white rounded-2xl shadow-md border border-slate-200 flex items-center justify-center p-8" style={{
        backfaceVisibility: 'hidden'
      }} data-id="element-135">
          <div className="text-3xl font-light text-slate-900 tracking-tight" data-id="element-136">
            {company.name}
          </div>
        </div>

        {/* Back of card - Description */}
        <div className="absolute inset-0 bg-slate-900 rounded-2xl shadow-md flex items-center justify-center p-8" style={{
        backfaceVisibility: 'hidden',
        transform: 'rotateY(180deg)'
      }} data-id="element-137">
          <p className="text-white text-center text-sm leading-relaxed font-light" data-id="element-138">
            {company.description}
          </p>
        </div>
      </motion.div>
    </motion.div>;
}
export function LogoCarousel() {
  const [isPaused, setIsPaused] = useState(false);
  const [offset, setOffset] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const cardWidth = 256 + 32; // 256px (w-64) + 32px gap
  const totalWidth = cardWidth * companies.length;
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setOffset(prev => {
        const newOffset = prev - 1;
        // Reset when we've scrolled past the first set
        if (Math.abs(newOffset) >= totalWidth) {
          return 0;
        }
        return newOffset;
      });
    }, 30);
    return () => clearInterval(interval);
  }, [isPaused, totalWidth]);
  const handlePrev = () => {
    setOffset(prev => {
      const newOffset = prev + cardWidth;
      // Wrap around if we go too far forward
      if (newOffset > 0) {
        return -(totalWidth - cardWidth);
      }
      return newOffset;
    });
  };
  const handleNext = () => {
    setOffset(prev => {
      const newOffset = prev - cardWidth;
      // Reset when we've scrolled past the first set
      if (Math.abs(newOffset) >= totalWidth) {
        return 0;
      }
      return newOffset;
    });
  };
  // Duplicate companies for seamless infinite scroll
  const duplicatedCompanies = [...companies, ...companies, ...companies];
  return <div className="relative overflow-hidden" onMouseEnter={() => {
    setIsPaused(true);
    setShowControls(true);
  }} onMouseLeave={() => {
    setIsPaused(false);
    setShowControls(false);
  }} data-id="element-139">
      <motion.div className="flex gap-8" animate={{
      x: offset
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 30
    }} data-id="element-140">
        {duplicatedCompanies.map((company, index) => <LogoCard key={`${company.name}-${index}`} company={company} index={index % companies.length} data-id="element-141" />)}
      </motion.div>

      {/* Navigation Controls */}
      <AnimatePresence data-id="element-142">
        {showControls && <>
            <motion.button initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.2
        }} onClick={handlePrev} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg border border-slate-200 flex items-center justify-center hover:bg-white hover:scale-110 transition-all z-10" aria-label="Previous" data-id="element-143">
              <ChevronLeft className="text-slate-700" size={24} data-id="element-144" />
            </motion.button>

            <motion.button initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }} transition={{
          duration: 0.2
        }} onClick={handleNext} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg border border-slate-200 flex items-center justify-center hover:bg-white hover:scale-110 transition-all z-10" aria-label="Next" data-id="element-145">
              <ChevronRight className="text-slate-700" size={24} data-id="element-146" />
            </motion.button>
          </>}
      </AnimatePresence>
    </div>;
}